import React from 'react';
import { User, Search, Trash2, FileDown } from 'lucide-react';
import { format } from 'date-fns';
import { Pagination } from '../../../components/common/Pagination';
import { useLanguage } from '../../../features/language/useLanguage';
import type { ArtisanProfileListItemDTO, ArtisanProfileFilters } from '../../../services/artisanProfileService';

interface Props {
    profiles: ArtisanProfileListItemDTO[] | undefined;
    isLoading: boolean;
    filters: ArtisanProfileFilters;
    onFiltersChange: (update: (f: ArtisanProfileFilters) => ArtisanProfileFilters) => void;
    page: number;
    size: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onSelectProfile: (id: string) => void;
    /** Selection is only rendered when the viewer can delete. */
    canDelete: boolean;
    selectedIds: Set<string>;
    onToggleSelect: (id: string) => void;
    onToggleSelectAll: () => void;
    onDeleteSelected: () => void;
    /** Deletes a single row; only wired when the viewer can delete. */
    onDeleteProfile: (id: string) => void;
    isDeleting: boolean;
}

export const ArtisanProfileTable: React.FC<Props> = ({
    profiles,
    isLoading,
    filters,
    onFiltersChange,
    page,
    size,
    total,
    onPageChange,
    onPageSizeChange,
    onSelectProfile,
    canDelete,
    selectedIds,
    onToggleSelect,
    onToggleSelectAll,
    onDeleteSelected,
    onDeleteProfile,
    isDeleting,
}) => {
    const { t } = useLanguage();

    // A synced profile cannot be deleted, so it is never selectable — "select all" must
    // agree with the per-row rule or the header checkbox would offer a doomed selection.
    const selectableIds = (profiles ?? []).filter(p => !p.is_synced).map(p => p.id);
    const allSelectableSelected =
        selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));

    // Base column count, plus the selection column when it is shown. Kept in one place so
    // the empty and loading rows keep spanning the whole table.
    const columnCount = (canDelete ? 1 : 0) + 6;

    return (
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex flex-wrap gap-3 sm:gap-4 items-center">
                <select
                    className="border-gray-300 rounded-md text-sm"
                    value={filters.status}
                    onChange={(e) => onFiltersChange(f => ({ ...f, status: e.target.value }))}
                >
                    <option value="all">{t('artisanSync.filters.allStatus')}</option>
                    <option value="synced">{t('artisanSync.filters.syncedToWo')}</option>
                    <option value="unsynced">{t('artisanSync.filters.unsynced')}</option>
                </select>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder={t('artisanSync.filters.searchRoaster')}
                        className="pl-9 border-gray-300 rounded-md text-sm"
                        value={filters.roaster || ''}
                        onChange={(e) => onFiltersChange(f => ({ ...f, roaster: e.target.value }))}
                    />
                </div>

                {canDelete && selectedIds.size > 0 && (
                    <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                            {t('artisanSync.delete.selectedCount', { count: selectedIds.size })}
                        </span>
                        <button
                            onClick={onDeleteSelected}
                            disabled={isDeleting}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            data-test-id="artisan-bulk-delete"
                        >
                            <Trash2 className="h-4 w-4" />
                            {t('artisanSync.delete.selected', { count: selectedIds.size })}
                        </button>
                    </div>
                )}
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {canDelete && (
                                <th className="px-4 py-3 w-10">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-primary-600"
                                        checked={allSelectableSelected}
                                        disabled={selectableIds.length === 0}
                                        onChange={onToggleSelectAll}
                                        aria-label={t('artisanSync.delete.selectAll')}
                                        data-test-id="artisan-profile-select-all"
                                    />
                                </th>
                            )}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('artisanSync.table.dateTime')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('artisanSync.table.roastTitle')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('artisanSync.table.roaster')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('artisanSync.table.machine')}</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('artisanSync.table.status')}</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('artisanSync.table.action')}</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {isLoading ? (
                            <tr><td colSpan={columnCount} className="px-6 py-12 text-center text-gray-500">{t('artisanSync.loadingProfiles')}</td></tr>
                        ) : profiles?.length === 0 ? (
                            <tr><td colSpan={columnCount} className="px-6 py-12 text-center text-gray-500">{t('artisanSync.emptyProfiles')}</td></tr>
                        ) : (
                            profiles?.map((profile: ArtisanProfileListItemDTO) => (
                                <tr
                                    key={profile.id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => onSelectProfile(profile.id)}
                                >
                                    {canDelete && (
                                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                                disabled={profile.is_synced}
                                                // Explained on the control itself rather than on click: the user
                                                // should learn the rule without attempting the action.
                                                title={profile.is_synced ? t('artisanSync.delete.syncedTooltip') : undefined}
                                                checked={selectedIds.has(profile.id)}
                                                onChange={() => onToggleSelect(profile.id)}
                                                data-test-id="artisan-profile-select"
                                            />
                                        </td>
                                    )}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {profile.roast_date ? format(new Date(profile.roast_date), 'MMM d, yyyy') : '-'}
                                        </div>
                                        <div className="text-sm text-gray-500">{profile.roast_time || '-'}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900 font-medium flex items-center gap-2">
                                            {profile.title || t('artisanSync.values.untitledRoast')}
                                            {profile.has_raw_file && (
                                                <FileDown
                                                    className="h-3.5 w-3.5 text-gray-400 flex-shrink-0"
                                                    aria-label={t('artisanSync.file.available')}
                                                />
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900 flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-gray-400" />{profile.roaster || '-'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-500 mt-1">{profile.machine || '-'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {profile.is_synced ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                {t('artisanSync.filters.synced')}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                {t('artisanSync.filters.unsynced')}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end gap-3">
                                            <button
                                                className="text-primary-600 hover:text-primary-900"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSelectProfile(profile.id);
                                                }}
                                            >
                                                {t('artisanSync.actions.view')}
                                            </button>
                                            {canDelete && (
                                                <button
                                                    className="text-red-600 hover:text-red-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                                                    disabled={profile.is_synced || isDeleting}
                                                    // Same rule as the row checkbox: a synced profile belongs to a
                                                    // work order, so the reason is stated on the control itself.
                                                    title={profile.is_synced ? t('artisanSync.delete.syncedTooltip') : t('artisanSync.delete.action')}
                                                    aria-label={t('artisanSync.delete.action')}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDeleteProfile(profile.id);
                                                    }}
                                                    data-test-id="artisan-profile-row-delete"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <Pagination page={page} pageSize={size} total={total} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} isLoading={isLoading} />
        </div>
    );
};
